/**
 * In-app dialogs — the replacement for `window.prompt` / `window.confirm`.
 *
 * Two reasons this is a module of its own and imports nothing from `panels/`: controllers ask the
 * same questions the panels do, and a native `prompt()` freezes the whole page (the render loop,
 * the autosave timers) while it is up.
 *
 * The backend is swappable so tests answer synchronously (`stubDialogs` in the panel test helpers)
 * instead of driving a real `<dialog>`.
 */

export interface TextDialogOptions {
  title: string;
  label?: string;
  value?: string;
  placeholder?: string;
  confirmLabel?: string;
  /** Return a message to block confirm, `null` to allow. Receives the TRIMMED value. */
  validate?: (value: string) => string | null;
}

export interface ConfirmDialogOptions {
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  danger?: boolean;
}

export interface PickDialogOptions {
  title: string;
  options: ReadonlyArray<{ value: string; label: string; meta?: string }>;
  value?: string;
  confirmLabel?: string;
  emptyMessage?: string;
}

export interface DialogBackend {
  /** Resolves the trimmed text; empty or cancelled → `null`. */
  text(o: TextDialogOptions): Promise<string | null>;
  confirm(o: ConfirmDialogOptions): Promise<boolean>;
  /** Resolves the chosen option's `value`; cancelled or nothing to pick → `null`. */
  pick(o: PickDialogOptions): Promise<string | null>;
}

/* --- the DOM backend ------------------------------------------------------- */

/** Unique ids for the `label[for]` pairing; a dialog can be open while another is queued. */
let seq = 0;

interface DialogSpec<T> {
  kind: 'text' | 'confirm' | 'pick';
  title: string;
  confirmLabel: string;
  cancelLabel: string;
  danger: boolean;
  /** What cancel, Escape and a native close resolve with. */
  cancelValue: T;
  body: HTMLElement[];
  /** Focused (and text-selected) once the dialog is up. */
  focus?: HTMLElement;
  read: () => T;
  /** Non-null message blocks confirm; re-run on every `input` in the body. */
  validate?: () => string | null;
  /** Nothing to confirm (an empty pick list), independent of `validate`. */
  confirmDisabled?: boolean;
}

function runDialog<T>(host: HTMLElement, spec: DialogSpec<T>): Promise<T> {
  const doc = host.ownerDocument;
  const dialog = doc.createElement('dialog');
  dialog.className = 'dlg';
  dialog.dataset.dlgKind = spec.kind;

  const heading = doc.createElement('h2');
  heading.textContent = spec.title;

  const body = doc.createElement('div');
  body.className = 'dlg-body';
  for (const child of spec.body) body.appendChild(child);

  const error = doc.createElement('p');
  error.className = 'dlg-error';
  error.hidden = true;
  body.appendChild(error);

  const cancelButton = doc.createElement('button');
  cancelButton.type = 'button';
  cancelButton.dataset.dlg = 'cancel';
  cancelButton.textContent = spec.cancelLabel;

  const confirmButton = doc.createElement('button');
  confirmButton.type = 'button';
  confirmButton.dataset.dlg = 'confirm';
  confirmButton.className = spec.danger ? 'primary danger' : 'primary';
  confirmButton.textContent = spec.confirmLabel;

  const actions = doc.createElement('div');
  actions.className = 'dlg-actions';
  actions.append(cancelButton, confirmButton);

  dialog.append(heading, body, actions);

  return new Promise<T>((resolve) => {
    let settled = false;
    let backdrop: HTMLElement | null = null;

    const finish = (value: T): void => {
      if (settled) return;
      settled = true;
      // `close()` re-enters through the `close` listener; the flag above makes that a no-op.
      try {
        if (dialog.open && typeof dialog.close === 'function') dialog.close();
      } catch {
        /* a backend-less <dialog> (very old jsdom) has nothing to close */
      }
      dialog.remove();
      backdrop?.remove();
      resolve(value);
    };

    const refresh = (): void => {
      const message = spec.validate?.() ?? null;
      error.textContent = message ?? '';
      error.hidden = message === null;
      confirmButton.disabled = message !== null || spec.confirmDisabled === true;
    };

    const tryConfirm = (): void => {
      if (confirmButton.disabled) return;
      finish(spec.read());
    };

    cancelButton.addEventListener('click', () => finish(spec.cancelValue));
    confirmButton.addEventListener('click', tryConfirm);
    body.addEventListener('input', refresh);

    /*
     * A modal owns the keyboard. Every key stops here, or Space plays the timeline and Delete
     * eats a keyframe behind the dialog — the app's shortcut handlers sit on `window`/`document`
     * in the bubble phase. Escape is handled here too: jsdom fires no native `cancel`.
     */
    dialog.addEventListener('keydown', (event) => {
      event.stopPropagation();
      if (event.key === 'Enter') {
        /*
         * Enter belongs to the FOCUSED BUTTON when there is one: Tab to Cancel, Enter, and a
         * danger dialog must cancel — not delete the clip. The click is issued here rather than
         * left to the platform, because the native activation would then fire a SECOND time
         * (and jsdom synthesizes none at all, so the behaviour would be untestable).
         */
        const focused = event.target;
        event.preventDefault();
        if (focused instanceof HTMLButtonElement && dialog.contains(focused)) focused.click();
        else tryConfirm();
      } else if (event.key === 'Escape') {
        event.preventDefault();
        finish(spec.cancelValue);
      }
    });

    // Closed by the platform (a second Escape path, a form submit): never leave this pending.
    dialog.addEventListener('cancel', (event) => {
      event.preventDefault();
      finish(spec.cancelValue);
    });
    dialog.addEventListener('close', () => finish(spec.cancelValue));

    host.appendChild(dialog);

    let modal = false;
    try {
      if (typeof dialog.showModal === 'function') {
        dialog.showModal();
        modal = true;
      }
    } catch {
      /* jsdom: "Not implemented: HTMLDialogElement.prototype.showModal" */
    }
    if (!modal) {
      dialog.setAttribute('open', '');
      backdrop = doc.createElement('div');
      backdrop.className = 'dlg-backdrop';
      dialog.parentNode?.insertBefore(backdrop, dialog);
    }

    refresh();
    const target = spec.focus ?? confirmButton;
    target.focus();
    if (target instanceof HTMLInputElement) target.select();
  });
}

function textDialog(host: HTMLElement, options: TextDialogOptions): Promise<string | null> {
  const doc = host.ownerDocument;
  const id = `dlg-in-${++seq}`;
  const input = doc.createElement('input');
  input.type = 'text';
  input.className = 'dlg-input';
  input.id = id;
  input.value = options.value ?? '';
  if (options.placeholder) input.placeholder = options.placeholder;

  const body: HTMLElement[] = [];
  if (options.label) {
    const label = doc.createElement('label');
    label.className = 'dlg-label';
    label.htmlFor = id;
    label.textContent = options.label;
    body.push(label);
  }
  body.push(input);

  return runDialog<string | null>(host, {
    kind: 'text',
    title: options.title,
    confirmLabel: options.confirmLabel ?? 'OK',
    cancelLabel: 'Cancel',
    danger: false,
    cancelValue: null,
    body,
    focus: input,
    read: () => input.value.trim() || null,
    validate: options.validate ? () => options.validate?.(input.value.trim()) ?? null : undefined,
  });
}

function confirmDialog(host: HTMLElement, options: ConfirmDialogOptions): Promise<boolean> {
  const message = host.ownerDocument.createElement('p');
  message.className = 'dlg-msg';
  message.textContent = options.message;

  return runDialog<boolean>(host, {
    kind: 'confirm',
    title: options.title,
    confirmLabel: options.confirmLabel ?? 'OK',
    cancelLabel: options.cancelLabel ?? 'Cancel',
    danger: options.danger === true,
    cancelValue: false,
    body: [message],
    read: () => true,
  });
}

function pickDialog(host: HTMLElement, options: PickDialogOptions): Promise<string | null> {
  const doc = host.ownerDocument;
  const empty = options.options.length === 0;

  const body: HTMLElement[] = [];
  let chosen = (): string | null => null;

  if (empty) {
    const message = doc.createElement('p');
    message.className = 'dlg-msg';
    message.textContent = options.emptyMessage ?? 'Nothing to choose from.';
    body.push(message);
  } else {
    const picker = doc.createElement('select');
    picker.className = 'dlg-select';
    for (const option of options.options) {
      const item = doc.createElement('option');
      item.value = option.value;
      item.textContent = option.meta ? `${option.label} · ${option.meta}` : option.label;
      picker.appendChild(item);
    }
    // Assign after the options exist, otherwise the browser drops an unknown value.
    picker.value = options.value ?? options.options[0].value;
    body.push(picker);
    chosen = () => picker.value || null;
  }

  return runDialog<string | null>(host, {
    kind: 'pick',
    title: options.title,
    confirmLabel: options.confirmLabel ?? 'OK',
    cancelLabel: 'Cancel',
    danger: false,
    cancelValue: null,
    body,
    focus: empty ? undefined : body[body.length - 1],
    read: () => chosen(),
    confirmDisabled: empty,
  });
}

/**
 * The default backend. `host` is resolved per call so a backend built at module load still lands
 * in the document that exists when the question is actually asked.
 *
 * Calls are serialized: two panels asking at once would otherwise stack two modals, and the second
 * `showModal()` would steal the focus from the first.
 */
export function createDomDialogBackend(host?: HTMLElement): DialogBackend {
  let queue: Promise<unknown> = Promise.resolve();
  const target = (): HTMLElement => host ?? document.body;

  function serialize<T>(run: (h: HTMLElement) => Promise<T>): Promise<T> {
    const next = queue.then(
      () => run(target()),
      () => run(target()),
    );
    queue = next.catch(() => undefined);
    return next;
  }

  return {
    text: (options) => serialize((h) => textDialog(h, options)),
    confirm: (options) => serialize((h) => confirmDialog(h, options)),
    pick: (options) => serialize((h) => pickDialog(h, options)),
  };
}

/* --- the installed backend ------------------------------------------------- */

let installed: DialogBackend | null = null;
let domBackend: DialogBackend | null = null;

function active(): DialogBackend {
  if (installed) return installed;
  domBackend ??= createDomDialogBackend();
  return domBackend;
}

/** Ask a question. Always await it — every backend is async, including the stub. */
export const dialogs: DialogBackend = {
  text: (options) => active().text(options),
  confirm: (options) => active().confirm(options),
  pick: (options) => active().pick(options),
};

/** `null` restores the default DOM backend. Tests call this from `cleanup()`. */
export function setDialogBackend(backend: DialogBackend | null): void {
  installed = backend;
}
